import { Result, Tagging } from '@empathy/search-types';
import { deepMerge } from '@empathybroker/deep-merge';
import { Observable } from 'rxjs/Observable';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { EVENTS } from '../../bus/events.const';
import { EmpathyXConfig, QueryOrigin } from '../../models';
import { ACTIONS_KEYS, GETTERS_KEYS, MUTATIONS_KEYS } from '../../store';
import { hasSearchQuery } from '../pipes/has-search-query.operator';
import { createWires } from '../wiring.util';
import { WIRE_FACTORY } from './wires.factory';

declare global {
  interface Window {
    pendingTrackQuery: () => void;
  }
}

export const TRACKING_WIRES = createWires({
  trackQuery(observable, { store }) {
    const { tracking: { query: { debounceTimeInMs } } }: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
    // Since the observable with the debounceTime can not be forced to run before the debounce time is up,
    // we need a way to do the pending track query. Then we store the track function in a global variable
    // to run it from other wire (trackPendingQuery), and use the same variable as flag to know if the
    // track is already done.
    const trackQueryFunction = () => {
      const hasFallbackFiltersResults: boolean = store.getters[GETTERS_KEYS.search.showFallbackFilters];
      if (!hasFallbackFiltersResults) {
        const queryTagging = store.state.search.queryTagging;
        store.dispatch(ACTIONS_KEYS.tracking.trackQuery, queryTagging);
      }
    };
    observable.subscribe(() => window.pendingTrackQuery = trackQueryFunction);

    observable.pipe(debounceTime(debounceTimeInMs)).subscribe(() => {
      if (window.pendingTrackQuery) {
        window.pendingTrackQuery = undefined;
        trackQueryFunction();
      }
    });
  },
  trackPendingQuery(observable) {
    observable.subscribe(() => {
      if (window.pendingTrackQuery) {
        window.pendingTrackQuery();
        window.pendingTrackQuery = undefined;
      }
    });
  },
  trackQueryAndEmitResultClicked(observable: Observable<Result>, { store, emit }) {
    observable.subscribe(result => {
      store.dispatch(ACTIONS_KEYS.tracking.trackQuery, result.tagging.query);
      emit(EVENTS.ResultClicked, result);
    });
  },
  trackBackFromPDPQuery(observable: Observable<Result>, { store }) {
    // With the default trackQuery, the queryOrigin doesn't change when there is cache enabled because it needs a search request first.
    // That is why we have to create this wire, to change the queryOrigin manually when there is not a search request.
    observable.pipe(hasSearchQuery(store)).subscribe(result => {
      const queryTagging: Tagging = deepMerge({}, store.state.search.queryTagging);
      queryTagging.params.origin = QueryOrigin.backFromPDP;
      store.dispatch(ACTIONS_KEYS.tracking.trackQuery, queryTagging);
    });
  },
  trackResultsEnd: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackResultsEnd),
  saveItemToLocalStorageWithURL: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.saveItemToLocalStorageWithURL),
  trackClick: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackClick),
  saveResultToLocalStorage: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.saveResultToLocalStorage),
  moveResultToSessionStorage: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.moveResultToSessionStorage),
  loadResultId: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.loadResultId),
  trackAdd2cartOnProductPage: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackAddToCartOnProductPage),
  trackAdd2cartOnResult: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackAddToCartOnResult),
  trackToggleWishlist: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackToggleWishlist),
  trackPendingURLClick: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackPendingURLClick),
  saveRightClickedResultIdToLocalStorage: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.saveResultIdToLocalStorage),
  trackOpen: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackOpen),
  trackClose: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackClose),
  trackFilter: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackFilter),
  trackClearFilters: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackClearFilters),
  trackSort: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackSort),
  trackColumnsChange: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackColumnsChange),
  disableResultsEnd: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.tracking.setResultsEnd, false),
  enableResultsEnd: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.tracking.setResultsEnd, true),
  trackCheckout: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.trackCheckout),
  addResultToLocalStorageCheckout: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.addResultToLocalStorageCheckout),
  removeLocalStorageCheckout: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.tracking.removeLocalStorageCheckout)
});
